import type { ReactNode } from 'react';
import { Logo } from '../components/Logo';

/** Where app users and readers reach the newsroom. */
const CONTACT_EMAIL = 'info@von.gov.ng';

/**
 * Public contact page for the VON News and VON iPlayer apps, served at
 * `https://vonnews.ng/contact-us` — no sign-in.
 *
 * Play's News and Magazines policy requires a dedicated, easy-to-find contact
 * page on the publisher's site as well as inside the app, showing an email
 * address or phone number. VON iPlayer was rejected for its absence (the
 * declared `von.gov.ng/contact` returns 404), so this is the URL to enter in
 * the Play Console's store listing contact details and in the News and
 * Magazines declaration. Each app's More screen carries the same address.
 *
 * Keep the address here in step with the apps' More screens and with the
 * "Contact us" section of the privacy policy.
 */
export default function ContactUs(): JSX.Element {
  return (
    <main className="min-h-screen bg-surface px-5 py-12">
      <article className="mx-auto w-full max-w-2xl text-[15px] leading-relaxed text-ink">
        <Logo size={36} withWordmark />

        <h1 className="mt-8 font-heading text-[32px] font-semibold leading-tight tracking-tight">
          Contact us
        </h1>
        <p className="mt-2 text-[13px] text-ink-muted">
          Voice of Nigeria Broadcasting Service · VON News and VON iPlayer apps
        </p>

        <P>
          The Voice of Nigeria Broadcasting Service (“VON”) is Nigeria’s external
          broadcaster, publishing news and radio in English, French, Arabic, Swahili, Fula,
          Hausa, Igbo and Yoruba. VON publishes the VON News and VON iPlayer apps, and every
          article and broadcast in them is produced by VON’s own newsroom.
        </P>

        <H2>Email</H2>
        <P>
          Write to{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-brand hover:underline">
            {CONTACT_EMAIL}
          </a>{' '}
          for anything about the apps, our reporting, or corrections. This address reaches
          the newsroom and the team that publishes the apps.
        </P>

        <H2>Newsroom and editorial</H2>
        <P>
          To reach editors about a story — a correction, a right of reply, or a tip — use the
          same address and put the headline in the subject line. VON News also has an{' '}
          <strong>Eyewitness report</strong> feature, under More in the app, for sending
          photos, video and an account of something you have witnessed directly to our
          editors.
        </P>

        <H2>Address</H2>
        <P>Voice of Nigeria Broadcasting Service, Abuja, Nigeria.</P>

        <H2>Online</H2>
        <P>
          Our main website is{' '}
          <a href="https://von.gov.ng" className="text-brand hover:underline">
            von.gov.ng
          </a>
          . Our{' '}
          <a href="/privacy" className="text-brand hover:underline">
            privacy policy
          </a>{' '}
          explains what the apps collect and the choices you have.
        </P>
      </article>
    </main>
  );
}

function H2({ children }: { children: ReactNode }): JSX.Element {
  return <h2 className="mt-10 font-heading text-[22px] font-semibold tracking-tight">{children}</h2>;
}

function P({ children }: { children: ReactNode }): JSX.Element {
  return <p className="mt-3 text-ink-muted">{children}</p>;
}
